/**
 * CatalogSyncPage — Task 25.5 (DEC-116). PLATFORM_ADMIN OCC Sync Management.
 *
 * Per-agency sync status cards (status, last/next sync, interval, Edit + Trigger),
 * a "N changes waiting for review" banner, the change-review queue (reuses the
 * Task 24.9 endpoints), and the run history (expand a FAILED row for its error).
 *
 * Run history starts collapsed to a one-line summary, and each viewer's open/closed
 * choice is remembered. One run a day used to push the review queue below the fold,
 * so a waiting change went unseen.
 */
import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Pill, subText } from '../../ui/bits';
import { SyncErrorText } from './SyncErrorText';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import {
  useSyncConfigs,
  useSyncRuns,
  useLatestSyncRun,
  useFailedSyncRunCount,
  useChangeQueue,
  RUNS_STEP,
  RUNS_MAX_LIMIT,
  type SyncConfig,
  type SyncRun,
} from './hooks';
import { EditIntervalModal, TriggerConfirmModal, agencyLabel } from './SyncModals';
import { ChangeReviewQueue } from './ChangeReviewQueue';
import { runDurationLabel } from './run-duration';
import {
  SYNCING_AGENCIES,
  downloadFailure,
  fileOutcomeLabel,
  groupSourceFiles,
  fileOutcomeTone,
} from './download-status';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** Local-by-context date/time (DEC-004 — never render UTC). */
function fmtDateTime(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Local date only ("Oct 11"). */
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Relative time ("3 days ago"). */
function timeAgo(iso: string | null): string {
  if (iso === null) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function statusTone(status: string | null): 'accent' | 'positive' | 'danger' | 'warning' | 'muted' {
  switch (status) {
    case 'RUNNING':
      return 'accent';
    case 'SUCCESS':
      return 'positive';
    case 'FAILED':
      return 'danger';
    case 'PARTIAL':
      return 'warning';
    default:
      return 'muted';
  }
}

function SyncCard({
  config,
  onEdit,
  onTrigger,
}: {
  config: SyncConfig;
  onEdit: (c: SyncConfig) => void;
  onTrigger: (agency: string) => void;
}): React.JSX.Element {
  const syncs = (SYNCING_AGENCIES as readonly string[]).includes(config.stateAgency);
  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600 }}>{agencyLabel(config.stateAgency)}</span>
        <Pill
          label={config.isActive ? 'Active' : 'Inactive'}
          tone={config.isActive ? 'positive' : 'muted'}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
        <span style={subText}>Last sync {timeAgo(config.lastSyncAt)}</span>
        {config.lastSyncStatus !== null && (
          <Pill label={config.lastSyncStatus} tone={statusTone(config.lastSyncStatus)} />
        )}
      </div>
      {config.lastSyncError !== null && <SyncErrorText error={config.lastSyncError} />}
      <DownloadStatus config={config} />
      <div style={{ ...subText, fontSize: '13px', textTransform: 'none' }}>
        {config.lastSyncRecordsAdded ?? 0} records added · {config.lastSyncChangesFlagged ?? 0}{' '}
        changes flagged
      </div>
      <div style={{ ...subText, fontSize: '13px', textTransform: 'none' }}>
        Every {config.syncIntervalDays} days · next {fmtDateTime(config.nextSyncAt)}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <Button type="button" variant="outline" onClick={(): void => onEdit(config)}>
          Edit interval
        </Button>
        {syncs ? (
          <Button
            type="button"
            onClick={(): void => onTrigger(config.stateAgency)}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            Trigger sync now
          </Button>
        ) : (
          <span style={{ ...subText, alignSelf: 'center', textTransform: 'none' }}>
            Parser not yet built
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * RRC plan PR 3 (Corbin: "show when the file fails to download"): a failed download in
 * plain words, when the files were last fetched successfully, and each file's state.
 */
function DownloadStatus({ config }: { config: SyncConfig }): React.JSX.Element | null {
  const run = config.lastRun;
  if (run == null) return null;
  const failure = downloadFailure(config.stateAgency, run);
  return (
    <>
      {failure !== null && (
        <div
          role="alert"
          style={{
            fontSize: '13px',
            padding: '8px 10px',
            borderRadius: '12px',
            border: '1px solid var(--color-danger)',
            backgroundColor: 'rgba(243, 139, 139,0.10)',
          }}
        >
          <strong>Download failed:</strong> {failure.file} — {failure.reason},{' '}
          {fmtDateTime(failure.at)}
        </div>
      )}
      <div style={{ ...subText, fontSize: '13px', textTransform: 'none' }}>
        Last successful download {timeAgo(config.lastSuccessfulDownloadAt ?? null)}
      </div>
      {run.sourceFiles.length > 0 && (
        <ul
          aria-label={`${agencyLabel(config.stateAgency)} source files`}
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '4px' }}
        >
          {groupSourceFiles(run.sourceFiles).map((f) => (
            <li
              key={f.name}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}
            >
              <span style={{ fontFamily: 'monospace' }}>{f.name}</span>
              <Pill label={fileOutcomeLabel(f.outcome)} tone={fileOutcomeTone(f.outcome)} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

const REVIEW_QUEUE_ANCHOR = 'change-review-queue';

/** "1 change waiting for review →" at the top of the page. Nothing when none are pending. */
function PendingChangesBanner(): React.JSX.Element | null {
  const pending = useChangeQueue('PENDING').data?.changes.length ?? 0;
  if (pending === 0) return null;
  const jump = (): void => {
    const target = document.getElementById(REVIEW_QUEUE_ANCHOR);
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    target?.focus({ preventScroll: true });
  };
  return (
    <button
      type="button"
      onClick={jump}
      style={{
        width: '100%',
        minHeight: '56px',
        marginBottom: '16px',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        textAlign: 'left',
        fontSize: '15px',
        fontWeight: 600,
        backgroundColor: 'rgba(246, 182, 94,0.14)',
        border: '1px solid var(--color-warning)',
        borderRadius: 'var(--radius-pill)',
        color: 'var(--color-warning)',
        cursor: 'pointer',
      }}
    >
      <span>
        {pending} change{pending === 1 ? '' : 's'} waiting for review
      </span>
    </button>
  );
}

/** Per-viewer Run History open/closed choice. Storage can be missing or throw (private mode). */
export const RUN_HISTORY_EXPANDED_KEY = 'lodex.admin.catalogSync.runHistoryExpanded';

function readExpanded(): boolean {
  try {
    return window.localStorage.getItem(RUN_HISTORY_EXPANDED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeExpanded(expanded: boolean): void {
  try {
    window.localStorage.setItem(RUN_HISTORY_EXPANDED_KEY, String(expanded));
  } catch {
    // Not remembered this time; the toggle still works.
  }
}

const FAILED_WINDOW_DAYS = 30;

/** "last run Oct 11 SUCCESS · 9 failed in last 30 days", or as much of it as has loaded. */
function RunHistorySummary(): React.JSX.Element | null {
  // Fixed at mount: it is part of the failed-count query key, so it must not move every render.
  const [since] = React.useState(() =>
    new Date(Date.now() - FAILED_WINDOW_DAYS * 86_400_000).toISOString(),
  );
  const latest = useLatestSyncRun().data?.runs[0];
  const failed = useFailedSyncRunCount(since).data?.total;
  if (latest === undefined && failed === undefined) return null;
  return (
    <span
      data-testid="run-history-summary"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
    >
      {latest !== undefined && (
        <>
          <span style={{ ...subText, textTransform: 'none' }}>
            last run {latest.stateAgency} {fmtDate(latest.startedAt)}
          </span>
          <Pill label={latest.status ?? 'UNKNOWN'} tone={statusTone(latest.status)} />
        </>
      )}
      {failed !== undefined && (
        <span
          style={{
            ...subText,
            textTransform: 'none',
            color: failed > 0 ? 'var(--color-danger)' : undefined,
          }}
        >
          {latest !== undefined ? '· ' : ''}
          {failed} failed in last {FAILED_WINDOW_DAYS} days
        </span>
      )}
    </span>
  );
}

function RunHistory(): React.JSX.Element {
  const [expanded, setExpanded] = React.useState(readExpanded);
  const [limit, setLimit] = React.useState(RUNS_STEP);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const { data, isLoading } = useSyncRuns(limit, expanded);
  const runs = data?.runs ?? [];
  const total = data?.total ?? 0;
  const bodyId = React.useId();

  const toggle = (): void => {
    const next = !expanded;
    setExpanded(next);
    writeExpanded(next);
  };

  return (
    <section style={{ marginTop: '28px' }}>
      <h3 style={{ margin: 0 }}>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={toggle}
          style={{
            width: '100%',
            minHeight: '56px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            textAlign: 'left',
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: 'var(--radius-pill)',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
          }}
        >
          {expanded ? (
            <ChevronDown size={18} aria-hidden="true" />
          ) : (
            <ChevronRight size={18} aria-hidden="true" />
          )}
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Run history</span>
          <RunHistorySummary />
        </button>
      </h3>
      {expanded && (
        <div id={bodyId} style={{ marginTop: '12px' }}>
          {isLoading ? (
            <p style={subText}>Loading…</p>
          ) : (
            <RunTable
              runs={runs}
              total={total}
              limit={limit}
              openId={openId}
              onToggleRow={(id): void => setOpenId(openId === id ? null : id)}
              onShowMore={(): void => setLimit((l) => Math.min(RUNS_MAX_LIMIT, l + RUNS_STEP))}
            />
          )}
        </div>
      )}
    </section>
  );
}

function RunTable({
  runs,
  total,
  limit,
  openId,
  onToggleRow,
  onShowMore,
}: {
  runs: readonly SyncRun[];
  total: number;
  limit: number;
  openId: string | null;
  onToggleRow: (id: string) => void;
  onShowMore: () => void;
}): React.JSX.Element {
  const canShowMore = runs.length < total && limit < RUNS_MAX_LIMIT;
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agency</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Records Added</TableHead>
            <TableHead>Changes Flagged</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((r) => (
            <React.Fragment key={r.id}>
              <TableRow
                onClick={(): void => onToggleRow(r.id)}
                style={{ cursor: r.status === 'FAILED' ? 'pointer' : 'default' }}
              >
                <TableCell>{r.stateAgency}</TableCell>
                <TableCell style={subText}>{fmtDateTime(r.startedAt)}</TableCell>
                <TableCell>{runDurationLabel(r)}</TableCell>
                <TableCell>
                  <Pill label={r.status ?? 'UNKNOWN'} tone={statusTone(r.status)} />
                </TableCell>
                <TableCell>{r.recordsAdded ?? 0}</TableCell>
                <TableCell>{r.changesFlagged ?? 0}</TableCell>
              </TableRow>
              {openId === r.id && r.errorMessage !== null && (
                <TableRow>
                  <TableCell colSpan={6} style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
                    {r.errorMessage}
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
          {runs.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} style={{ ...subText, textAlign: 'center', padding: '24px' }}>
                No sync runs yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {runs.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '12px',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <span style={{ ...subText, textTransform: 'none' }}>
            Showing the latest {runs.length} of {formatNumber(total)}
          </span>
          {canShowMore && (
            <Button
              type="button"
              variant="outline"
              onClick={onShowMore}
              style={{ minHeight: '56px' }}
            >
              Show more
            </Button>
          )}
        </div>
      )}
    </>
  );
}

export function CatalogSyncPage(): React.JSX.Element {
  const { data, isLoading, isError } = useSyncConfigs();
  const [editConfig, setEditConfig] = React.useState<SyncConfig | null>(null);
  const [triggerAgency, setTriggerAgency] = React.useState<string | null>(null);
  const configs = data?.configs ?? [];

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Catalog Sync</h2>

      <PendingChangesBanner />

      {isLoading && <p style={subText}>Loading sync status…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load sync status. Refresh to try again.
        </p>
      )}

      {!isLoading && !isError && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px',
          }}
        >
          {configs.map((c) => (
            <SyncCard key={c.id} config={c} onEdit={setEditConfig} onTrigger={setTriggerAgency} />
          ))}
        </div>
      )}

      {/* The review queue sits above Run History: a long history used to push a waiting change
          off-screen (Corbin, 2026-10-15). */}
      <div id={REVIEW_QUEUE_ANCHOR} tabIndex={-1} style={{ outline: 'none' }}>
        <ChangeReviewQueue />
      </div>
      <RunHistory />

      <EditIntervalModal config={editConfig} onOpenChange={(): void => setEditConfig(null)} />
      <TriggerConfirmModal
        agency={triggerAgency}
        onOpenChange={(): void => setTriggerAgency(null)}
      />
    </div>
  );
}
